import { regionHasAdminPortal, regionServesGenericApp } from "./siteHelpers";

const normalize = (region?: string | null): string =>
  (region ?? "").trim().toLowerCase();

export interface RegionLandingDecision {
  // The legacy/home region, normalized ("" when absent).
  homeRegion: string;
  // The user's accessible regions that have a real regional portal, deduped.
  accessibleRegions: string[];
  // Region to auto-redirect to, or null when the user must pick / has none.
  targetRegion: string | null;
  // Home region to serve the admin app IN-PLACE for (dev/test/unlinked accounts on the generic
  // dashboard), or null when the home region routes to a real portal / has no served scope.
  scopedRegion: string | null;
}

/**
 * Decide where a user landing on the generic admin dashboard should go, given
 * their backend profile's legacy `region` and full `regions` (= getAllRegions()).
 *
 * Rules (per the decided design):
 *  - Prefer the legacy home region when it maps to a real portal.
 *  - Otherwise, if exactly one accessible region has a portal, go there.
 *  - Otherwise return null: the caller shows a chooser (>1) or a message (0).
 *
 * Region strings are trimmed + lowercased because the backend's getAllRegions()
 * splits its CSV without trimming, so values can arrive space-padded.
 */
export const resolveRegionLanding = (
  region?: string | null,
  regions?: string[] | null
): RegionLandingDecision => {
  const homeRegion = normalize(region);

  const seen = new Set<string>();
  const accessibleRegions: string[] = [];
  (regions ?? []).forEach((r) => {
    const n = normalize(r);
    if (n && regionHasAdminPortal(n) && !seen.has(n)) {
      seen.add(n);
      accessibleRegions.push(n);
    }
  });

  const targetRegion = regionHasAdminPortal(homeRegion)
    ? homeRegion
    : accessibleRegions.length === 1
    ? accessibleRegions[0]
    : null;

  // A non-franchise home region (unassigned / dev*) is served in-place rather than routed.
  const scopedRegion = regionServesGenericApp(homeRegion) ? homeRegion : null;

  return { homeRegion, accessibleRegions, targetRegion, scopedRegion };
};
